import React, { useState, useEffect, useCallback } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import queryString from 'query-string';
import { Box, Grid } from '@material-ui/core';
import { Dimmer } from 'semantic-ui-react';
import useForm from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import Wizard from '../../components/Wizard';
import { STEP_IDS, steps } from './recurringSteps';
import {
  addAttachCardPayerAndSaveInCache,
  attachNewCard,
} from '../../redux/modules/payment';
import { clearBasket } from '../../redux/modules/basketReducer';
import { clearContract } from '../../redux/modules/recPayment';
import PolicySummary from './PolicySummary';
import EfesContentHeader from '../../components/EfesContentHeader';
import Loader from '../../components/Loaders/Loader';
import ScrollToTopPage from '../../components/ScrollToTopPage';
import useDeviceResize from '../../hooks/useDeviceResize';
import { FULL_WIDTH } from '../../constants/uiConstants';
import { CONTRACT_SUBSCRIPTION_ATTACH_CARD_PATH } from '../../constants/routes';
import { attachSubscriptionCardSchema } from '../../validations/contract/attachSubscriptionCardSchema';
import useRegisterValidationFields from '../../hooks/useRegisterValidationFields';
import { ATTACH_PAYMENT_CARD_OPTIONS } from '../../constants';

import attachCard from '../../img/svgs/payment/attachCard.svg';
import useStepNavigationAmountStyles from '../../components/MUI/StepNavigationAmount/makeStyles';

const getUnlockedSteps = (recovering) => {
  if (recovering) {
    return [STEP_IDS.POLICY, STEP_IDS.ATTACH];
  }
  return [STEP_IDS.POLICY];
};

const AttachCardToPolicyContainer = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const isDeviceLgSize = useDeviceResize('lg');
  const stepNavigationAmountClasses = useStepNavigationAmountStyles();
  const basket = useSelector((store) => store.basketReducer.basket, shallowEqual);
  const agreement = useSelector((store) => store.paymentReducer.agreement);
  const recovering = useSelector((store) => store.paymentReducer.recoveringRecurringPayment);
  const { contract } = useSelector((store) => store.recPayment, shallowEqual);

  const [stepNavigationBox, setStepNavigationBox] = useState({});
  const [urlParams] = useState(queryString.parse(location.hash));
  const [attachCardAgreement, setAttachCardAgreement] = useState(agreement || false);
  const [openMobileSummary, setOpenMobileSummary] = useState();
  const [currentStep, setCurrentStep] = useState(steps[0]);
  const [unlockedSteps, setUnlockedSteps] = useState(getUnlockedSteps(urlParams.bfp === 'true'));

  const {
    register,
    handleSubmit,
    errors,
    setValue,
    triggerValidation,
    formState,
    getValues,
  } = useForm({
    validationSchema: attachSubscriptionCardSchema,
    mode: 'onBlur',
  });

  useRegisterValidationFields(['attachCardAgreement'], register);

  useEffect(() => {
    setValue('attachCardAgreement', attachCardAgreement);
  }, [attachCardAgreement, setValue]);

  useEffect(() => {
    setAttachCardAgreement(agreement || !!contract.attachedCard);
    setValue('attachCardAgreement', agreement);
  }, [agreement, contract, setValue]);

  const handleNext = useCallback(
    () => {
      dispatch(attachNewCard(ATTACH_PAYMENT_CARD_OPTIONS.ATTACH_EXISTING_RECURRING_CONTRACT_CARD));
    },
    [dispatch],
  );

  const onSubmit = useCallback(
    (data) => {
      dispatch(addAttachCardPayerAndSaveInCache({ ...data, attachCardAgreement }))
        .then(() => {
          handleNext();
        })
        .catch(() => { });
    },
    [dispatch, handleNext, attachCardAgreement],
  );

  const handleOpenMobileSummary = useCallback(() => {
    setOpenMobileSummary(!openMobileSummary);
  }, [openMobileSummary]);

  useEffect(() => {
    if (getValues().attachCardAgreement) {
      triggerValidation({ name: 'attachCardAgreement' });
    }
  }, [triggerValidation, getValues]);

  const handlePayClick = useCallback(() => {
    if (!getValues().attachCardAgreement) {
      triggerValidation({ name: 'attachCardAgreement' });
    } else if (currentStep.id === STEP_IDS.POLICY) {
      history.push({
        pathname: CONTRACT_SUBSCRIPTION_ATTACH_CARD_PATH,
      });
      setCurrentStep(steps[1]);
      setUnlockedSteps([STEP_IDS.POLICY, STEP_IDS.ATTACH]);
    } else if (currentStep.id === STEP_IDS.ATTACH) {
      handleSubmit(onSubmit)();
    }
  }, [
    history,
    currentStep,
    handleSubmit,
    onSubmit,
    setCurrentStep,
    setUnlockedSteps,
    triggerValidation,
    getValues,
  ]);

  const stepParams = {
    payerContactFormProps: { register, errors, setValue, triggerValidation, formState },
    openMobileSummary,
    handleOpenMobileSummary,
    setStepNavigationBox,
    handlePayClick,
    payBtnTitle: t('automaticRecurringPayment.attach'),
    attachCardAgreement,
    setAttachCardAgreement,
    setAttachCardAgreementValue: setValue,
    attachCardAgreementErrors: errors,
  };

  useEffect(() => () => {
    dispatch(clearContract());
    dispatch(clearBasket());
    setValue('policyNumber', '');
  },
  [dispatch, setValue]);

  return (
    <>
      <ScrollToTopPage />
      <EfesContentHeader
        headerText="automaticRecurringPayment.headerTitle"
        headerImage={attachCard}
        imageWidth={63}
      />
      {recovering && (
        <Dimmer active inverted>
          <Loader />
        </Dimmer>
      )}
      <Grid container>
        <Grid
          item
          style={{
            width: FULL_WIDTH,
          }}
        >
          <Wizard
            stepContainers={steps}
            stepParams={stepParams}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            unlockedSteps={unlockedSteps}
            setUnlockedSteps={setUnlockedSteps}
          />
        </Grid>
        {
        !isDeviceLgSize && !basket[0]?.isFullyPaid && (
        <Grid
          item
          style={{
            width: FULL_WIDTH,
          }}
        >
          <PolicySummary
            openMobileSummary={openMobileSummary}
            onDismiss={() => setOpenMobileSummary(!openMobileSummary)}
            handlePayClick={handlePayClick}
            attachCardAgreement={attachCardAgreement}
            setAttachCardAgreement={setAttachCardAgreement}
            basket={basket}
            currentStep={currentStep}
            contract={contract}
            formProps={{ errors, setValue }}
          />
        </Grid>
        )
      }
      </Grid>
      {isDeviceLgSize && (
        <Grid
          container
          className={cn({
            [stepNavigationAmountClasses.mobileNavigation]: isDeviceLgSize,
          })}
          spacing={isDeviceLgSize ? 4 : 0}
        >
          <Grid item style={{ width: FULL_WIDTH }}>
            <Box>{!isEmpty(stepNavigationBox) && stepNavigationBox}</Box>
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default AttachCardToPolicyContainer;
